// Products Module Types

import type { ProductRole } from '../../../../generated/config-enums';
import type { TagSummary } from '../../../../shared/components/tags';

export type { ProductRole };

export type ProductType = 'physical' | 'service' | 'subscription' | 'bundle';
export type BillingCycle = 'one_time' | 'monthly' | 'quarterly' | 'annual' | 'custom';
export type PricingModel = 'flat' | 'tiered' | 'volume' | 'graduated';
export type ServiceBillingModel = 'fixed' | 'hourly' | 'project' | 'retainer' | 'metered';
export type ConfigType = 'text' | 'number' | 'select' | 'multi_select' | 'date';
export type PriceAdjustmentType = 'none' | 'fixed' | 'percent';
export type AggregationType = 'sum' | 'max' | 'last';
export type ProductStatus = 'draft' | 'planned' | 'active' | 'archived' | 'discontinued';
export type CommissionType = 'none' | 'percent' | 'flat';

export interface ProductCategoryRef {
  id: string;
  name: string;
}

export interface Product {
  id: string;
  tenant_id: string;
  /** Category memberships (UI exposes a single category = 0-or-1-element array). */
  product_category_ids: string[];
  /** Populated when listed/fetched with include_categories. */
  categories?: ProductCategoryRef[];
  name: string;
  sku: string | null;
  product_type: ProductType;
  /** 1:1 link to the offerings supertype (OFF-1); null until registered. */
  offering_id: string | null;
  description: string | null;
  /** Brand / manufacturer (Shopify "Vendor"). Free-text; not the supplier relationship. */
  brand: string | null;
  base_price: number | null;
  cost_price: number | null;
  billing_cycle: string | null;
  pricing_model: string | null;
  service_billing_model: string | null;
  service_duration_hours: number | null;
  tax_category: string | null;
  is_taxable: boolean;
  /** Label for the implicit base unit (used when a line item has packaging_id = null). */
  base_unit_name: string;
  url: string | null;
  hs_code: string | null;
  /** Main product image (FK to a file). Null = no image. */
  image_id: string | null;
  // Physical product dimensions (stored in metric: kg, cm)
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  // Per-record unit overrides (Runtime Units of Measure, Tier A). Metric values
  // above stay canonical; these only name the display/entry unit for this
  // product. Null = inherit user/tenant preference.
  weight_unit: 'kg' | 'lb' | null;
  dimension_unit: 'cm' | 'in' | null;
  status: ProductStatus;
  /** Buy/sell/both classification: sell = sales catalogue, buy = purchased/office
   * supplies, both = either. Drives the products-list Sell/Buy filter. */
  product_role: ProductRole;
  /** Restricted/specialty product kept off the main catalog (exportable as a separate hidden list). */
  is_hidden: boolean;
  is_deleted: boolean;
  company_ids: string[];
  // Default packaging selections (per-product UoM lives on ProductPackaging)
  default_sales_packaging_id: string | null;
  default_purchase_packaging_id: string | null;
  default_stock_packaging_id: string | null;
  // T9 Product Master Enrichment — catalog codes
  upc: string | null;
  gtin: string | null;
  web_catalog_enabled: boolean;
  is_quick_product: boolean;
  // Sourcing
  substitute_product_id: string | null;
  // Commission
  commission_type: CommissionType;
  commission_rate: number | null;
  // Default GL accounts (per-warehouse overrides: ProductWarehouseAccount)
  revenue_account_id: string | null;
  cogs_account_id: string | null;
  inventory_account_id: string | null;
  // Costs — DECIMAL dollars
  last_cost: number | null;
  third_party_logistics_cost: number | null;
  minimum_price: number | null;
  // Physical / logistics
  is_catch_weight: boolean;
  cubic_feet: number | null;
  pallet_ti: number | null;
  pallet_hi: number | null;
  // Hazardous material
  is_hazardous: boolean;
  hazmat_class: string | null;
  hazmat_un_number: string | null;
  // Seasonal availability (recurring annual window, month 1-12)
  is_seasonal: boolean;
  season_start_month: number | null;
  season_end_month: number | null;
  /** Tenant-defined custom fields (P4); shape governed by custom_field_definitions. */
  custom_fields: Record<string, unknown>;
  /** Cross-cutting tags framework; populated server-side on fetch/list. */
  tags?: TagSummary[];
  /**
   * Sharing (rollout Contract C/D): the caller's best relation on this product,
   * or null. Absent/null in everyone mode (⇒ full access); populated in a
   * restricted visibility mode (owner_and_shared / team).
   */
  user_relation?: RelationType | null;
  created_at: string;
  updated_at: string;
}

// Read shapes generated from JSON Schema (shared/schemas/master_data/*.schema.json):
// - ProductVendorSku: per-vendor sourcing identifier for a product (GET /products/{id}/vendor-skus).
// - VendorProductOffering: the products a vendor offers, reciprocal of ProductVendorSku,
//   enriched with product master fields (GET /vendor-skus/by-vendor/{vendor_organization_id}).
// - ProductTaxCategory: tenant-managed vocabulary for products.tax_category, with the
//   augmented product_count read field (GET /tax-categories).
// - VendorCategoryCoverage / CategoryVendorCoverage: reciprocal reads of the
//   vendor ↔ product-category sourcing coverage (which vendor supplies which KIND
//   of thing). CategoryCoverageEntry: one entry in the tenant-wide resolved
//   coverage map that ranks the RFQ supplier picker. Shortlist/ranking hint only.
export type { CategoryCoverageEntry } from '../../../../generated/api/models/categoryCoverageEntry';
export type { CategoryVendorCoverage } from '../../../../generated/api/models/categoryVendorCoverage';
export type { ProductTaxCategory } from '../../../../generated/api/models/productTaxCategory';
export type { ProductVendorSku } from '../../../../generated/api/models/productVendorSku';
export type { VendorCategoryCoverage } from '../../../../generated/api/models/vendorCategoryCoverage';
export type { VendorProductOffering } from '../../../../generated/api/models/vendorProductOffering';
import type { RelationType } from '../../../../generated/api/models/relationType';

/**
 * The offering-supertype kind. Single-sourced from the generated enum
 * (shared/config/product_catalog.config.json → offering_kind): product | bundle
 * | shipment_service. A resolved reference is `product`/`bundle` for a catalog
 * line and `shipment_service` for a product-less freight service line.
 */
export type { OfferingKind } from '../../../../generated/api/models/offeringKind';
import type { OfferingKind } from '../../../../generated/api/models/offeringKind';

export interface ProductVendorSkuCreate {
  vendor_organization_id: string;
  vendor_sku?: string | null;
  vendor_product_id?: string | null;
  is_primary?: boolean;
  lead_time_days?: number | null;
  notes?: string | null;
}

export interface ProductVendorSkuUpdate {
  vendor_sku?: string | null;
  vendor_product_id?: string | null;
  is_primary?: boolean;
  lead_time_days?: number | null;
  notes?: string | null;
}

/** Per-warehouse GL account override for a product (T9). */
export interface ProductWarehouseAccount {
  id: string;
  tenant_id: string;
  product_id: string;
  warehouse_id: string;
  revenue_account_id: string | null;
  cogs_account_id: string | null;
  inventory_account_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface ProductWarehouseAccountCreate {
  warehouse_id: string;
  revenue_account_id?: string | null;
  cogs_account_id?: string | null;
  inventory_account_id?: string | null;
}

export interface ProductWarehouseAccountUpdate {
  revenue_account_id?: string | null;
  cogs_account_id?: string | null;
  inventory_account_id?: string | null;
  clear_revenue_account?: boolean;
  clear_cogs_account?: boolean;
  clear_inventory_account?: boolean;
}

export type PackagingType =
  | 'box' | 'pallet' | 'bag' | 'drum' | 'bottle' | 'case' | 'crate'
  | 'carton' | 'can' | 'pouch' | 'sack' | 'container' | 'tube' | 'jar'
  | 'roll' | 'other';

export const PACKAGING_TYPES: PackagingType[] = [
  'box', 'pallet', 'bag', 'drum', 'bottle', 'case', 'crate', 'carton',
  'can', 'pouch', 'sack', 'container', 'tube', 'jar', 'roll', 'other',
];

export interface ProductPackaging {
  id: string;
  tenant_id: string;
  product_id: string;
  unit_name: string;
  quantity_in_base: number;
  packaging_type: PackagingType | null;
  sku: string | null;
  image_id: string | null;
  barcode: string | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductPackagingCreate {
  unit_name: string;
  quantity_in_base?: number;
  packaging_type?: PackagingType | null;
  sku?: string | null;
  image_id?: string | null;
  barcode?: string | null;
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  sort_order?: number;
}

export interface ProductPackagingUpdate {
  unit_name?: string;
  quantity_in_base?: number;
  packaging_type?: PackagingType | null;
  sku?: string | null;
  image_id?: string | null;
  barcode?: string | null;
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  sort_order?: number;
}

export interface ProductDefaultPackagings {
  default_sales_packaging_id?: string | null;
  default_purchase_packaging_id?: string | null;
  default_stock_packaging_id?: string | null;
}

export interface ProductPreview {
  id: string;
  name: string;
  sku: string | null;
}

export interface ProductCategory {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  image_id: string | null;
  status: ProductStatus;
  product_count?: number;
  product_previews?: ProductPreview[];
  created_at: string;
  updated_at: string;
}

/** A category tree node — same shape as ProductCategory plus nested children. */
export interface ProductCategoryTreeNode extends ProductCategory {
  children: ProductCategoryTreeNode[];
}

/** An allowed value of a variant dimension (e.g. "Red", "S"). */
export interface ProductDimensionValue {
  id: string;
  tenant_id: string;
  dimension_id: string;
  value: string;
  sku_code: string;
  position: number;
  source_value_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * A variant dimension (axis) such as Color or Size. `product_id` is null for
 * tenant-level library templates and set for product-scoped dimensions.
 */
export interface ProductDimension {
  id: string;
  tenant_id: string;
  product_id: string | null;
  name: string;
  position: number;
  source_dimension_id: string | null;
  values: ProductDimensionValue[];
  created_at: string;
  updated_at: string;
}

/** A variant's selected value for one of its product's dimensions. */
export interface ProductVariantOption {
  id: string;
  variant_id: string;
  dimension_id: string;
  dimension_name: string | null;
  value_id: string;
  value: string | null;
  sku_code: string | null;
}

/**
 * One axis of a variant's denormalized search/display cache: the value label
 * plus the canonical library value id. Derived from `options`; never authored
 * directly. Mirrors `product_variants.dimension_values`.
 */
export interface ProductVariantDimensionValue {
  dimension_id: string;
  dimension_name: string | null;
  value_id: string;
  value: string;
  library_value_id: string | null;
}

export interface ProductVariant {
  id: string;
  tenant_id: string;
  product_id: string;
  name: string;
  sku: string;
  options: ProductVariantOption[];
  dimension_values: ProductVariantDimensionValue[];
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  /** Per-variant unit cost (Shopify "Cost per item"). */
  cost: number | null;
  /** Per-variant MSRP / list price (Shopify "Compare At Price"). */
  compare_at_price: number | null;
  /** Per-variant GTIN/UPC/EAN/ISBN barcode. */
  barcode: string | null;
  image_id: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/** An allowed value of a select / multi_select configuration. */
export interface ProductConfigurationValue {
  id: string;
  tenant_id: string;
  configuration_id: string;
  value: string;
  label: string | null;
  price_adjustment_type: PriceAdjustmentType;
  price_adjustment: number | null;
  is_default: boolean;
  sort_order: number;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * A per-product configuration field, chosen when the product is referenced.
 * `inventory_tracked` decides downstream behaviour: true = stock-tracked (the
 * value defines an inventory lot — expiry, lot/batch number); false = post-pick
 * (a line-item payload only — engraving, gift message).
 */
export interface ProductConfiguration {
  id: string;
  tenant_id: string;
  /** Owner scope: exactly one of product_id / category_id is set. */
  product_id: string | null;
  category_id: string | null;
  name: string;
  code: string;
  description: string | null;
  config_type: ConfigType;
  inventory_tracked: boolean;
  is_required: boolean;
  min_value: number | null;
  max_value: number | null;
  min_length: number | null;
  max_length: number | null;
  min_selections: number | null;
  max_selections: number | null;
  price_adjustment_type: PriceAdjustmentType;
  price_adjustment: number | null;
  sort_order: number;
  status: string;
  values: ProductConfigurationValue[];
  created_at: string;
  updated_at: string;
  /**
   * Inheritance provenance (present on effective-list responses). When
   * `is_inherited` the config is owned by an ancestor category and is read-only
   * at this scope; `inherited_from_category_*` name that category.
   */
  is_inherited?: boolean;
  inherited_from_category_id?: string | null;
  inherited_from_category_name?: string | null;
}

export interface PriceList {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  currency_code: string;
  country_codes: string[];
  customer_segment: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_default: boolean;
  /** Whether this list's prices are tax/VAT inclusive (gross) or exclusive (net). */
  prices_include_tax: boolean;
  is_deleted: boolean;
  product_count?: number;
  company_ids: string[];
  /**
   * Sharing (rollout Contract C/D): the caller's best relation on this price
   * list, or null. Absent/null in everyone mode (⇒ full access); populated in a
   * restricted visibility mode.
   */
  user_relation?: RelationType | null;
  created_at: string;
  updated_at: string;
}

export type BillingUnit = 'each' | 'user' | 'seat' | 'device' | 'site' | 'hour' | 'transaction' | string;
export type BillingPeriod = 'one_time' | 'monthly' | 'quarterly' | 'annual' | 'per_use' | null;

export interface PriceListEntry {
  id: string;
  tenant_id: string;
  price_list_id: string;
  /** OFF-7: offerings supertype handle (kind = product | bundle) — the sole catalog ref; product_id dropped. The physical product is resolved 1:1 from the offering for display. */
  offering_id: string;
  variant_id: string | null;
  packaging_id: string | null;
  unit_price: number; // DECIMAL dollars
  min_quantity: number;
  setup_fee: number | null; // DECIMAL dollars
  sort_order: number;
  billing_unit: BillingUnit;
  billing_period: BillingPeriod;
  discount_percent: number | null;
  pricing_model: PricingModel;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// ProductReference — the canonical product-reference contract.
// Mirrors shared/schemas/master_data/product_reference.schema.json. The single
// way to reference a product anywhere in PanLuma. Quantity/unit stay line-level.
// See docs/modules/master-data/product-reference-contract.md.
// ---------------------------------------------------------------------------

/** One post-pick configuration choice captured on a product reference. */
export interface ProductConfigurationSelection {
  configuration_id: string;
  /** The configuration's stable code. */
  configuration_code: string;
  config_type: ConfigType;
  /** Chosen value(s) — one for text/number/select/date, one or more for multi_select. */
  values: string[];
  /** Human-readable rendered value (e.g. 'Red, Blue' or 'Happy Birthday'). */
  display_value: string | null;
  /** Resolved total price adjustment in DECIMAL dollars for this selection. */
  price_adjustment: number;
}

/** A product reference — what is referenced, never how much. */
export interface ProductReference {
  /**
   * The catalog product. Nullable (OFF-7 makes offering_id the anchor): a
   * product-less freight service line (kind='shipment_service') carries an
   * offering_id + operational_instance_id and no product_id. Catalog lines
   * always set it. A reference must carry at least one of product_id / offering_id.
   */
  product_id: string | null;
  /**
   * The offerings supertype handle (OFF-2); kind = product | bundle today.
   * Optional/nullable — matches the JSON schema (not in `required`) and the
   * Python dataclass (`offering_id: UUID | None = None`). Resolvable from the
   * product when omitted; catalog-only consumers (e.g. inventory, excluded from
   * the offerings migration) legitimately omit it.
   */
  offering_id?: string | null;
  /** The materialised variant; encodes variant-dimension values. */
  variant_id: string | null;
  /** The packaging UoM; null = base unit. */
  packaging_id: string | null;
  /** The stock lot; encodes the stock-tracked configuration values. */
  lot_id: string | null;
  /** Chosen post-pick configuration values + resolved price adjustments. */
  configuration_selections: ProductConfigurationSelection[] | null;
  /**
   * TMS G3 (freight-as-product §1): the concrete operational instance that
   * realizes this offering line, in the offering's source_module. For
   * kind='shipment_service' it is a shp_shipments.id; null for discrete
   * products/services. No hard FK — resolved via the entity-URL registry.
   */
  operational_instance_id?: string | null;
}

/** A ProductReference expanded by the backend resolver. */
export interface ResolvedProductReference {
  reference: ProductReference;
  /**
   * The catalog product id. Null for a product-less service line
   * (kind='shipment_service') — the name is the offering label instead.
   */
  product_id: string | null;
  product_name: string;
  product_sku: string | null;
  /** The resolved offering handle (OFF-2); null until the product is registered. */
  offering_id: string | null;
  /**
   * The offering kind: 'product' | 'bundle' for a catalog line, 'shipment_service'
   * for a product-less freight service line.
   */
  offering_kind: OfferingKind;
  variant_id: string | null;
  variant_sku: string | null;
  /** Per variant axis — mirrors `product_variants.dimension_values`. */
  variant_dimensions: ProductVariantDimensionValue[];
  /**
   * IMG-1: the image to show for this reference (a file id) — the variant's own
   * image when set, else the product's main image; null when neither has one.
   * Resolve to a URL with `useFileUrl`.
   */
  image_id: string | null;
  /**
   * URL-1: the product's external page URL (`products.url`); null when unset.
   * No variant-level override — a reference resolves to one product.
   */
  product_url: string | null;
  packaging_id: string | null;
  packaging_name: string | null;
  quantity_in_base: number | null;
  lot_id: string | null;
  lot_code: string | null;
  lot_expiry_date: string | null;
  configuration_selections: ProductConfigurationSelection[];
  /**
   * TMS G3: the operational instance handle echoed from the reference (a
   * shp_shipments.id for kind='shipment_service'; null for products/services).
   */
  operational_instance_id: string | null;
  /**
   * TMS G3: the resolved in-app path to the operational instance (via the
   * entity-URL registry), e.g. `/shipments/{id}`; null for product/bundle lines.
   * The display links a chip to it when present.
   */
  operational_instance_url: string | null;
  /** Full composed label — matches the backend formatter. */
  display_name: string;
  /** Variant SKU if present, else product SKU. */
  effective_sku: string | null;
  /** Catalog/variant/packaging unit price before post-pick config adjustments (dollars). */
  unit_price_base: number | null;
  /** Sum of post-pick configuration price adjustments (dollars). */
  config_price_adjustment: number;
  /** unit_price_base + config_price_adjustment (dollars); null if no price resolved. */
  unit_price: number | null;
  /** Base-unit on-hand for the resolved lot (or product/variant); null if not stocked. */
  on_hand: number | null;
}

/** Build an empty reference for a freshly chosen product (or bundle).
 *
 * `offeringId` is the product's 1:1 offering handle (OFF-2). Pass it so the
 * emitted reference carries `offering_id`; it stays null for legacy callers and
 * the backend resolver still derives it from the product.
 */
export function emptyProductReference(
  productId: string,
  offeringId: string | null = null,
): ProductReference {
  return {
    product_id: productId,
    offering_id: offeringId,
    variant_id: null,
    packaging_id: null,
    lot_id: null,
    configuration_selections: null,
    operational_instance_id: null,
  };
}

/** Build a reference for a product-less offering (a kind='shipment_service'
 * freight line — TMS G4 / freight-as-product §1). It anchors on `offering_id`
 * alone; `product_id` and the product sub-layers stay null. The optional
 * `operationalInstanceId` binds the concrete shipment realizing the line (null
 * for a service-only line, which degrades to an ordinary line per §5). */
export function emptyServiceOfferingReference(
  offeringId: string,
  operationalInstanceId: string | null = null,
): ProductReference {
  return {
    product_id: null,
    offering_id: offeringId,
    variant_id: null,
    packaging_id: null,
    lot_id: null,
    configuration_selections: null,
    operational_instance_id: operationalInstanceId,
  };
}

/**
 * Build a ProductReference from a consumer's stored scalar fields, or null when
 * no product is set. The adapter for catalog-only consumers that persist only
 * `product_id` (and optionally `packaging_id`) — they pass this as the picker's
 * `value` and destructure `product_id` / `packaging_id` back out of `onChange`.
 */
export function productReferenceFromFields(
  productId: string | null | undefined,
  packagingId?: string | null,
  offeringId?: string | null,
): ProductReference | null {
  if (!productId) return null;
  return {
    product_id: productId,
    offering_id: offeringId ?? null,
    variant_id: null,
    packaging_id: packagingId ?? null,
    lot_id: null,
    configuration_selections: null,
    operational_instance_id: null,
  };
}

export interface ProductTypeOption {
  value: ProductType;
  label: string;
  color: string;
  icon: string;
}

export interface BillingCycleOption {
  value: BillingCycle;
  label: string;
  months: number;
}

export interface PricingModelOption {
  value: PricingModel;
  label: string;
  description: string;
}
